import React, { Component } from "react";
import PropTypes from "prop-types";
import dynamic from "next/dynamic";
import { connect } from "react-redux";
import RuleEvaluator from "@gcloud-npm/utils.rule-evaluator";
import SecureLocalStorage from "@gcloud-npm/utils.secure.local-storage";
import DetailCardList from "../cms-detail-card-list";
import apiFetch from "@gcloud-npm/adabyron.astro-fetch";
import getPage from "@gcloud-npm/adabyron.custo.get-page";
import { findOutEnvByEnvName } from "@gcloud-npm/utils.enums.environment";

const CustomLink = dynamic(() => import("@/bit/components/components.custom-link"));
const IconGlyph = dynamic(() => import("@/bit/components/components.icon-glyph"));

const currentEnv = `${process.env.NEXT_PUBLIC_ENV_NAME}`;

/**
 * Componente que permite crear un carrousel de DetailCard
 */
class CmsCustoDetailCardList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      reload: 0,
      componentProps: props["component-props"],
      finalFetch: false,
      name: props["activity-name"],
      customRules: props["component-props"]?.customRules || "",
    };
  }

  static getDerivedStateFromProps = (props, state) => {
    let { reload, finalFetch } = state;
    const { context, logged } = props;

    const contextLoaded = context?.contextSeoMeta;

    // solo me planteo algo si llega el contextLoaded, que tendrá lo que tenga que tener
    if (contextLoaded || props.forceReload) {
      if (logged) {
        reload = 1;
      } else {
        finalFetch = true;
      }
    }

    return {
      ...state,
      context,
      // variable uso interno para decirle al compo que debo pedir más info
      reload,
      // cuando ya tenemos toda la info clara y quiero dibujar en vista
      finalFetch,
    };
  };

  sendGtagEvent = (info) => {
    if (process.env.NEXT_PUBLIC_ENV_NAME === "prod") {
      gtag("event", "cms_component", info);
    }
  };

  withTimeout = (promise, ms) =>
    Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Request timeout")), ms),
      ),
    ]);

  /* --------------- Evaluar reglas y refrescar info con getPage -------------- */
  evaluateRules = async () => {
    const { name, componentProps } = this.state;
    let jsonRules = componentProps?.["cms-json-rules"] || null;

    try {
      // Extraemos secure
      const secureStorage = new SecureLocalStorage();
      const ts = secureStorage.getItem("ts");

      // Repido rules si no las tengo
      if (!jsonRules || Object.keys(jsonRules).length === 0) {
        const context = {
          environment: findOutEnvByEnvName(
            currentEnv === "prod" || currentEnv === "local" ? "prod" : "qa_ssr",
          ),
        };

        const page = await getPage({
          name,
          context,
        });

        if (
          page &&
          page[`${name}-RULES`] &&
          page[`${name}-RULES`]["cms-json-rules"]
        ) {
          jsonRules = page[`${name}-RULES`]["cms-json-rules"];
        } else {
          throw new Error("No se encontraron reglas que aplicar");
        }
      }

      // Evaluamos cada regla de las actividades para crear los parametros que enviar
      const rulesToApply = jsonRules.map((activity) => {
        const {
          "activity-name": activityName,
          "creativity-rules": creativityRules,
        } = activity;

        let ruleToApply = "";
        // En cada crea miramos que regla se cumple
        for (const creativity in creativityRules) {
          const { id, rules } = creativityRules[creativity];

          const ruleEvaluator = new RuleEvaluator(rules);
          const doesRuleApply = ruleEvaluator.test(ts);
          if (doesRuleApply) {
            ruleToApply = id;
            break;
          }
        }

        return ruleToApply;
      });

      const customRules = rulesToApply?.join("::");

      // Actualizamos info de página con reglas de cliente
      const normalizedPage = await this.withTimeout(
        apiFetch({
          method: "GET",
          url: "/api/astro-maker/v1/cms/get-customizer-page-content",
          params: {
            name,
            customRules,
          },
        }),
        1500,
      );

      this.setState({
        componentProps: normalizedPage,
        finalFetch: true,
        customRules,
      });
    } catch (error) {
      console.log("Error al recargar información de custo: ", error);
      this.setState({ finalFetch: true });
    }
  };

  componentDidMount = () => {
    const { name, finalFetch, reload } = this.state;

    const pathname = window.location.pathname;
    const shouldNotReload = pathname.includes("cambiar-mi-tarifa");

    if (!finalFetch && reload === 1 && name && !shouldNotReload) {
      this.evaluateRules();
    }

    if (shouldNotReload) {
      this.setState({ finalFetch: true });
    }
  };

  componentDidUpdate(prevProps, prevState) {
    const { name, reload, finalFetch, customRules } = this.state;
    const pathname = window.location.pathname;
    const shouldNotReload = pathname.includes("cambiar-mi-tarifa");

    if (!finalFetch && reload === 1 && name && prevState.reload !== reload) {
      this.evaluateRules();
    }

    if (
      shouldNotReload &&
      prevProps["component-props"] !== this.props["component-props"]
    ) {
      this.setState({
        componentProps: this.props["component-props"],
      });
    }

    // Envio evento para promo privada
    if (
      finalFetch &&
      customRules &&
      customRules.includes(
        "CMS-ASTRO-TARIFAS-MOVILES-ILIMITADA-PROMO-PRIVADA-DIGI",
      )
    ) {
      this.sendGtagEvent({
        component_name: "private-promotion",
        component_content: "tarifa-ilimitada-promo",
        component_perso: "none",
        component_product: " tarifa-ilimitada",
        component_variation: "card-distributiva",
        event_type: "print-success",
      });
    }
  }

  render() {
    const { cmsHandler, blockName, pageId } = this.props;
    const { componentProps, finalFetch } = this.state;

    return (
      <section id={blockName || ""}>
        {cmsHandler && (
          <>
            <span
              className={"element-editable block edit-component"}
              onClick={() =>
                cmsHandler({
                  ...this.props,
                  name: "cms-block.cms-custo-detail-card-list",
                })
              }
            >
              <IconGlyph id="lapiz-m" height={28} color="white" />
            </span>
            {pageId && (
              <CustomLink
                target="_blank"
                href={`https://admin.customizer.es/#/cmsPages/${pageId}`}
                className="editor-msg"
              >
                <span>
                  Pulse aquí para acceder a las actividades y reglas de este
                  componente 🔗
                </span>
              </CustomLink>
            )}
          </>
        )}
        <DetailCardList
          {...componentProps}
          cmsClassname={`custo`}
          blockName={"cms-component-detail-card-list-1"}
          contentLoaded={finalFetch}
          cmsHandler={cmsHandler}
          hideEditor={true}
        />
        <style jsx>{`.cms-component-custo-detail-card-list-1 {
  :global(.DetailCard) {
    :global(.content) {
      min-height: 215px;
    }
  }
  .editor-msg {
    position: absolute;
    top: 50px;
    left: 50%;
    transform: translate(-50%, -50%);

    background-color: #2196f3;
    color: #fff;
    padding: 12px 20px;
    border-radius: 8px;
    text-align: center;
    font-size: 18px;
    font-weight: 500;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
    z-index: 1000;

    display: flex;
    align-items: flex-start;
    gap: 0.5rem;

    .open-link {
      color: #fff;
      text-decoration: underline;
      cursor: pointer;
      display: inline;
      &:hover {
        color: #164972;
      }
    }
  }
}
`}</style>
      </section>
    );
  }
}

const mapStateToProps = (state) => ({
  context: state.main.context,
  logged: state.main.logged,
});

CmsCustoDetailCardList.propTypes = {
  /**
   * {
   *  "info": "Nombre de la CMS-ACTIVITY de la que leerá la info",
   *  "kind": "both",
   *  "beautifulName": "Activity Name"
   * }
   */
  "activity-name": PropTypes.string,
  /**
   * {
   *  "info": "Kind",
   *  "kind": "dev",
   *  "beautifulName": "Kind",
   *  "values": "./enums.js:KIND"
   * }
   */
  kind: PropTypes.string,
};

CmsCustoDetailCardList.defaultProps = {
  "activity-name": "",
  kind: "page",
};

export default connect(mapStateToProps, null)(CmsCustoDetailCardList);
